import { readCampaignAttribution } from "@/components/analytics/attribution";
import { readMarketingConsent } from "@/components/analytics/consent";
import { recordAnalyticsDebugEvent } from "@/components/analytics/debug-events";
import type { AnalyticsEventName } from "@/components/analytics/event-catalog";
import { initializeGoogle } from "@/components/analytics/google-analytics";
import { initializeMetaPixel } from "@/components/analytics/meta-pixel";

const configured = (value: string | undefined) =>
  value && value !== "not-set" ? value : "";
const COACHING_LEAD_SEND_TO =
  configured(import.meta.env.VITE_GOOGLE_ADS_LEAD_SEND_TO) ||
  (import.meta.env.VITE_APP_STAGE === "production"
    ? "AW-16798392352/Utg_CKmjs_McEKDAjMo-"
    : "");

import { create } from "zustand";
import { persist } from "zustand/middleware";

interface PurchaseTrackingStoreState {
  sentPaymentIds: Record<string, boolean>;
  hasSentPurchase: (paymentId: string) => boolean;
  markPurchaseSent: (paymentId: string) => void;
}

export const usePurchaseTrackingStore = create<PurchaseTrackingStoreState>()(
  persist(
    (set, get) => ({
      sentPaymentIds: {},
      hasSentPurchase: (paymentId) => Boolean(get().sentPaymentIds[paymentId]),
      markPurchaseSent: (paymentId) =>
        set((s) => ({
          sentPaymentIds: { ...s.sentPaymentIds, [paymentId]: true },
        })),
    }),
    {
      name: "thecoach_purchase_tracking",
    },
  ),
);

const PENDING_REGISTRATION_KEY = "thecoach_pending_registration";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function canSendAnalyticsEvent(
  name: AnalyticsEventName,
  params: Record<string, unknown>,
) {
  recordAnalyticsDebugEvent(name, params);
  return !import.meta.env.DEV && readMarketingConsent() === "granted";
}

export function readMetaBrowserIds() {
  if (typeof document === "undefined" || readMarketingConsent() !== "granted")
    return {} as { fbp?: string; fbc?: string };
  const cookies = Object.fromEntries(
    document.cookie.split("; ").map((part) => {
      const separator = part.indexOf("=");
      return separator < 0
        ? [part, ""]
        : [part.slice(0, separator), part.slice(separator + 1)];
    }),
  );
  return { fbp: cookies._fbp?.slice(0, 150), fbc: cookies._fbc?.slice(0, 150) };
}

export function readCheckoutMarketingFields() {
  const attribution =
    readMarketingConsent() === "granted" ? readCampaignAttribution() : null;
  const meta = readMetaBrowserIds();
  return {
    source: attribution?.source,
    medium: attribution?.medium,
    campaign: attribution?.campaign,
    gclid: attribution?.gclid,
    fbclid: attribution?.fbclid,
    metaFbp: meta.fbp,
    metaFbc: meta.fbc,
  };
}

export function trackCoachingLead(applicationId: string) {
  const params = {
    content_name: "Koçluk Başvurusu",
    application_id: applicationId,
  };
  if (!canSendAnalyticsEvent("coaching_lead", params)) return;
  initializeGoogle();
  initializeMetaPixel();

  if (COACHING_LEAD_SEND_TO)
    window.gtag?.("event", "conversion", {
      send_to: COACHING_LEAD_SEND_TO,
      transaction_id: applicationId,
      value: 1,
      currency: "TRY",
    });
  window.gtag?.("event", "generate_lead", {
    lead_source: "coaching_application",
    transaction_id: applicationId,
  });
  window.fbq?.("track", "Lead", params, { eventID: applicationId });
}

export function trackCheckoutStarted(
  paymentId: string,
  itemType: "package" | "program",
  itemId: string,
  value: number,
) {
  const params = {
    transaction_id: paymentId,
    currency: "TRY",
    value,
    item_type: itemType,
    item_id: itemId,
  };
  if (!canSendAnalyticsEvent("begin_checkout", params)) return;
  initializeGoogle();
  initializeMetaPixel();
  window.gtag?.("event", "begin_checkout", {
    currency: "TRY",
    value,
    items: [{ item_id: itemId, item_category: itemType }],
  });
  window.fbq?.(
    "track",
    "InitiateCheckout",
    { currency: "TRY", value, content_type: itemType, content_ids: [itemId] },
    { eventID: `checkout-${paymentId}` },
  );
}

export function trackVerifiedPurchase(receipt: {
  paymentId: string;
  itemType: "package" | "program";
  itemId: string;
  amount: number;
}) {
  const eventName =
    receipt.itemType === "package" ? "coaching_purchase" : "program_purchase";
  if (
    !canSendAnalyticsEvent(eventName, {
      transaction_id: receipt.paymentId,
      value: receipt.amount,
      currency: "TRY",
    })
  )
    return;
  initializeGoogle();
  initializeMetaPixel();
  if (usePurchaseTrackingStore.getState().hasSentPurchase(receipt.paymentId))
    return;
  if (!window.gtag && !window.fbq) return;
  const sendTo = configured(
    receipt.itemType === "package"
      ? import.meta.env.VITE_GOOGLE_ADS_PACKAGE_SEND_TO
      : import.meta.env.VITE_GOOGLE_ADS_PROGRAM_SEND_TO,
  );
  if (sendTo)
    window.gtag?.("event", "conversion", {
      send_to: sendTo,
      transaction_id: receipt.paymentId,
      value: receipt.amount,
      currency: "TRY",
    });
  window.gtag?.("event", "purchase", {
    transaction_id: receipt.paymentId,
    value: receipt.amount,
    currency: "TRY",
    items: [
      {
        item_id: receipt.itemId,
        item_category: receipt.itemType,
        price: receipt.amount,
        quantity: 1,
      },
    ],
  });
  window.fbq?.(
    "track",
    "Purchase",
    {
      value: receipt.amount,
      currency: "TRY",
      content_type: receipt.itemType,
      content_ids: [receipt.itemId],
    },
    { eventID: receipt.paymentId },
  );
  usePurchaseTrackingStore.getState().markPurchaseSent(receipt.paymentId);
}

export function trackCoachingFormStart(packageTitle: string) {
  const params = {
    form_name: "coaching_application",
    package_name: packageTitle,
  };
  if (!canSendAnalyticsEvent("form_start", params)) return;

  window.gtag?.("event", "form_start", params);
  window.fbq?.("trackCustom", "CoachingFormStart", params);
}

export function trackCoachingFormSubmit(packageTitle: string) {
  const params = {
    form_name: "coaching_application",
    package_name: packageTitle,
  };
  if (!canSendAnalyticsEvent("form_submit", params)) return;

  window.gtag?.("event", "form_submit", params);
  window.fbq?.("trackCustom", "CoachingFormSubmit", params);
}

export function trackCoachingFormError(packageTitle: string) {
  const params = {
    form_name: "coaching_application",
    package_name: packageTitle,
  };
  if (!canSendAnalyticsEvent("form_error", params)) return;

  window.gtag?.("event", "form_error", params);
  window.fbq?.("trackCustom", "CoachingFormError", params);
}

export function trackAccountRegistration(method: "email" | "google") {
  const params = { method };
  if (!canSendAnalyticsEvent("sign_up", params)) return;

  window.gtag?.("event", "sign_up", params);
  window.fbq?.("track", "CompleteRegistration", {
    content_name: "Hesap Oluşturma",
    registration_method: method,
  });
}

export function trackLoginAttempt(
  method: "password" | "magic_link" | "google",
) {
  const params = { method };
  if (!canSendAnalyticsEvent("login_attempt", params)) return;

  window.gtag?.("event", "login_attempt", params);
  window.fbq?.("trackCustom", "LoginAttempt", params);
}

export function trackLoginSuccess(method: "password") {
  const params = { method };
  if (!canSendAnalyticsEvent("login", params)) return;

  window.gtag?.("event", "login", params);
  window.fbq?.("trackCustom", "LoginSuccess", params);
}

export function trackLoginError(method: "password" | "magic_link") {
  const params = { method };
  if (!canSendAnalyticsEvent("login_error", params)) return;

  window.gtag?.("event", "login_error", params);
  window.fbq?.("trackCustom", "LoginError", params);
}

export function trackMagicLinkSent() {
  const params = { method: "magic_link" };
  if (!canSendAnalyticsEvent("login_link_sent", params)) return;

  window.gtag?.("event", "login_link_sent", params);
  window.fbq?.("trackCustom", "LoginLinkSent", params);
}

export function trackProgramJoinAttempt(source: "dialog" | "invite_page") {
  const params = { join_source: source };
  if (!canSendAnalyticsEvent("join_program_attempt", params)) return;

  window.gtag?.("event", "join_program_attempt", params);
  window.fbq?.("trackCustom", "JoinProgramAttempt", params);
}

export function trackProgramJoinError() {
  const params = { join_source: "invite_page" };
  if (!canSendAnalyticsEvent("join_program_error", params)) return;

  window.gtag?.("event", "join_program_error", params);
  window.fbq?.("trackCustom", "JoinProgramError", params);
}

export function markPendingAccountRegistration(method: "google") {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(PENDING_REGISTRATION_KEY, method);
}

export function trackPendingAccountRegistration() {
  if (typeof window === "undefined") return;
  const method = window.sessionStorage.getItem(PENDING_REGISTRATION_KEY);
  if (method !== "google") return;

  window.sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
  trackAccountRegistration(method);
}

export function trackProgramStart(programId: string, programName: string) {
  const params = { program_id: programId, program_name: programName };
  if (!canSendAnalyticsEvent("start_program", params)) return;

  window.gtag?.("event", "start_program", params);
  window.fbq?.("trackCustom", "StartProgram", params);
}

export function trackCalculatorComplete(calculatorName: string) {
  const params = { calculator_name: calculatorName };
  if (!canSendAnalyticsEvent("calculator_complete", params)) return;

  window.gtag?.("event", "calculator_complete", params);
  window.fbq?.("trackCustom", "CalculatorComplete", params);
}

export function trackContentView(
  contentType: "essay" | "projects",
  contentId: string,
) {
  const params = { content_type: contentType, content_id: contentId };
  if (!canSendAnalyticsEvent("view_content", params)) return;

  window.gtag?.("event", "view_content", params);
  window.fbq?.("track", "ViewContent", {
    content_type: contentType,
    content_ids: [contentId],
  });
}

export function trackProjectSupport(projectId: string, projectTitle: string) {
  const params = { project_id: projectId, project_name: projectTitle };
  if (!canSendAnalyticsEvent("support_project", params)) return;

  window.gtag?.("event", "support_project", params);
  window.fbq?.("trackCustom", "SupportProject", params);
}

export function trackContentEngagement(contentId: string) {
  const params = {
    content_type: "essay",
    content_id: contentId,
    engagement_time_seconds: 30,
  };
  if (!canSendAnalyticsEvent("content_engaged", params)) return;

  window.gtag?.("event", "content_engaged", params);
  window.fbq?.("trackCustom", "ContentEngaged", params);
}

export function trackCtaClick(ctaName: string, destination?: string) {
  const params = {
    cta_name: ctaName,
    ...(destination ? { destination } : {}),
  };
  if (!canSendAnalyticsEvent("cta_click", params)) return;

  window.gtag?.("event", "cta_click", params);
  window.fbq?.("trackCustom", "CtaClick", params);
}

export function trackCoachingDurationSelect(durationMonths: number) {
  const params = { duration_months: durationMonths };
  if (!canSendAnalyticsEvent("select_coaching_duration", params)) return;

  window.gtag?.("event", "select_coaching_duration", params);
  window.fbq?.("trackCustom", "SelectCoachingDuration", params);
}

export function trackContactClick(
  contactMethod: "email" | "phone" | "whatsapp",
) {
  const params = { contact_method: contactMethod };
  if (!canSendAnalyticsEvent("contact_click", params)) return;

  window.gtag?.("event", "contact_click", params);
  window.fbq?.("track", "Contact", params);
}

export function trackSocialClick(platform: string, destination: string) {
  const params = { platform, destination };
  if (!canSendAnalyticsEvent("social_click", params)) return;

  window.gtag?.("event", "social_click", params);
}
